import { useEffect, useState } from 'react';
import { getCore } from 'cvat-core-wrapper';

interface TaskSummary {
    taskName: string | null;
    projectId: number | null;
    projectName: string | null;
    size: number | null;
}

const EMPTY: TaskSummary = {
    taskName: null, projectId: null, projectName: null, size: null,
};

interface Entry {
    value: TaskSummary;
    listeners: Set<(value: TaskSummary) => void>;
    started: boolean;
}

// the bar remounts with every job; one load per task keeps the breadcrumb and the counts from flickering
const entries = new Map<number, Entry>();

function entryFor(taskId: number): Entry {
    let entry = entries.get(taskId);
    if (!entry) {
        entry = { value: EMPTY, listeners: new Set(), started: false };
        entries.set(taskId, entry);
    }
    return entry;
}

function load(taskId: number, entry: Entry): void {
    entry.started = true;
    const publish = (value: TaskSummary): void => {
        entry.value = value;
        entry.listeners.forEach((listener) => listener(value));
    };
    const core = getCore();
    (async () => {
        const [task] = await core.tasks.get({ id: taskId });
        if (!task) throw new Error('no task');
        const projectId = task.projectId ?? null;
        publish({
            taskName: task.name, projectId, projectName: null, size: task.size ?? null,
        });
        if (projectId === null) return;
        const [project] = await core.projects.get({ id: projectId });
        if (project) {
            publish({
                taskName: task.name, projectId, projectName: project.name, size: task.size ?? null,
            });
        }
    })().catch(() => {
        // the breadcrumb falls back to "Task #id"; the next mount tries again
        entry.started = false;
    });
}

export function useTaskSummary(taskId: number): TaskSummary {
    const [summary, setSummary] = useState<TaskSummary>(() => entryFor(taskId).value);

    useEffect(() => {
        const entry = entryFor(taskId);
        setSummary(entry.value);
        entry.listeners.add(setSummary);
        if (!entry.started) load(taskId, entry);
        return () => { entry.listeners.delete(setSummary); };
    }, [taskId]);

    return summary;
}
